// Quick-add sheet (mobile overhaul Phase 0 §0.4.5; mockup §1.2 E). The FAB
// always opens this. The focused field at the top is ⌘E's compose mode
// wearing a sheet — Enter creates the task due today with no project via
// the shared `createQuickTask` path. The rows below open the full forms,
// which render as sheets themselves through Modal's mobile branch.

import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { useRegisteredQuickAddAction } from '../lib/quickAdd';
import { createQuickTask } from '../lib/quickTask';
import type { ProjectWithClient } from '../../shared/types';
import { NewTaskModal } from '../pages/Tasks';
import { NewLeadModal } from '../pages/Leads';
import { NewProjectModal } from '../pages/Projects';
import { ExpenseModal } from '../pages/Finance';
import type { ProjectOption } from './TaskRow';
import { Sheet, SheetButton, SheetRow } from './Sheet';
import { ExpenseIcon, MLeadsIcon, MProjectsIcon, MTasksIcon } from './icons';

type Form = 'task' | 'lead' | 'project' | 'expense';

const NOTICE_MS = 650;

export default function QuickAddSheet({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const pageAction = useRegisteredQuickAddAction();
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [form, setForm] = useState<Form | null>(null);
  const [projects, setProjects] = useState<ProjectWithClient[]>([]);
  const timer = useRef<number | null>(null);
  // Bumped every time the sheet closes: a POST still in flight from the
  // previous opening must not clear or close whatever Henry typed next.
  const gen = useRef(0);

  // The forms need a project list; refetch on every open so a project
  // created this session shows up (same endpoint the pages use).
  useEffect(() => {
    if (!open) return;
    api<{ projects: ProjectWithClient[] }>('/api/admin/projects')
      .then((d) => setProjects(d.projects))
      .catch(() => {});
  }, [open]);

  useEffect(() => {
    if (open) return;
    gen.current += 1;
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = null;
    setTitle('');
    setNotice(null);
    setBusy(false);
  }, [open]);

  useEffect(
    () => () => {
      if (timer.current) window.clearTimeout(timer.current);
    },
    [],
  );

  const openForm = (next: Form) => {
    setForm(next);
    onClose();
  };

  // The forms POST through `api()`, which fires `ss:mutated` itself — the
  // callback only has to put the form away.
  const created = () => setForm(null);

  const submit = async () => {
    const trimmed = title.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    const g = gen.current;
    try {
      await createQuickTask(trimmed, null);
    } catch {
      if (g === gen.current) setBusy(false);
      return;
    }
    if (g !== gen.current) return;
    setTitle('');
    setBusy(false);
    setNotice('Task added — due today');
    timer.current = window.setTimeout(onClose, NOTICE_MS);
  };

  // A new task can't land on a voided project — leave them off the picker.
  const taskProjects: ProjectOption[] = projects
    .filter((p) => p.voided_at === null)
    .map((p) => ({ id: p.id, name: p.name, phase: p.phase }));

  return (
    <>
      {open && (
        <Sheet
          open
          onClose={onClose}
          variant="half"
          title="Quick add"
          rightAction={<SheetButton onClick={onClose}>Close</SheetButton>}
          className="quick-add-sheet"
        >
          <div className="qa-field">
            <label className="qa-label" htmlFor="qa-title">New task · due today</label>
            <div className="qa-input">
              <input
                id="qa-title"
                value={title}
                placeholder="Task title…"
                disabled={busy}
                onChange={(e) => setTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== 'Enter') return;
                  e.preventDefault();
                  void submit();
                }}
              />
              <span className="qa-hint">↵ ADD</span>
            </div>
            {notice ? (
              <div className="qa-notice">{notice}</div>
            ) : (
              <p className="qa-help">
                Enter adds it with no project. Tap “New task” below for project, date and priority.
              </p>
            )}
          </div>
          {pageAction && (
            <SheetRow
              icon={pageAction.icon}
              label={pageAction.label}
              right="›"
              onSelect={() => {
                onClose();
                pageAction.onSelect();
              }}
            />
          )}
          <SheetRow
            icon={<MTasksIcon />}
            label="New task"
            sub="Full form"
            right="›"
            onSelect={() => openForm('task')}
          />
          <SheetRow icon={<MLeadsIcon />} label="New lead" right="›" onSelect={() => openForm('lead')} />
          <SheetRow
            icon={<MProjectsIcon />}
            label="New project"
            right="›"
            onSelect={() => openForm('project')}
          />
          <SheetRow
            icon={<ExpenseIcon />}
            label="New expense"
            right="›"
            onSelect={() => openForm('expense')}
          />
        </Sheet>
      )}
      {/* Rendered outside the sheet: tapping a row closes it and leaves the
          form standing on top of the current page. */}
      {form === 'task' && (
        <NewTaskModal projects={taskProjects} onClose={() => setForm(null)} onCreated={created} />
      )}
      {form === 'lead' && <NewLeadModal onClose={() => setForm(null)} onCreated={created} />}
      {form === 'project' && <NewProjectModal onClose={() => setForm(null)} onCreated={created} />}
      {form === 'expense' && (
        <ExpenseModal
          initial={null}
          projects={projects}
          onClose={() => setForm(null)}
          onSaved={created}
        />
      )}
    </>
  );
}
